import axios from 'axios'
import type { AxiosError, InternalAxiosRequestConfig } from 'axios'
import type { AuthTokens } from '@/types'
import {
  endSupportSession,
  getSupportSessionToken,
  isSupportSession,
  markSupportSessionExpired,
} from '@/lib/supportSession'

const API_BASE_URL = import.meta.env.VITE_API_URL || ''

// Token storage keys
const REFRESH_TOKEN_KEY = 'flowrent_refresh_token'

// In-memory access token storage
let accessToken: string | null = null

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Token management functions
export const setTokens = (tokens: AuthTokens) => {
  accessToken = tokens.access_token
  localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refresh_token)
}

// EP-160 section 2.3: in a support tab the session token is the bearer.
export const getAccessToken = () => (isSupportSession() ? getSupportSessionToken() : accessToken)

export const getRefreshToken = () => localStorage.getItem(REFRESH_TOKEN_KEY)

export const clearTokens = () => {
  accessToken = null
  localStorage.removeItem(REFRESH_TOKEN_KEY)
}

export const hasRefreshToken = () => !!getRefreshToken()

// Request interceptor - add access token (exported for testing). In a support
// tab (EP-160) it sends the session token, never a user token, even when
// localStorage holds a user refresh token.
export function addAuthHeader(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  const token = getAccessToken()
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
}

// Single-flight refresh (WEB-H1): when N parallel requests 401 together they
// must share ONE refresh. Doorkeeper revokes the refresh token on first use,
// so concurrent refreshes race — the first wins and the rest get invalid_grant,
// which used to force a spurious logout whenever a multi-query page expired.
let refreshPromise: Promise<AuthTokens> | null = null

export async function refreshTokensSingleFlight(): Promise<AuthTokens> {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      const refreshToken = getRefreshToken()
      if (!refreshToken) throw new Error('no refresh token')
      let tokens: AuthTokens
      try {
        tokens = await refreshAccessToken(refreshToken)
      } catch {
        // First refresh failed — retry once after a short delay (transient 500s)
        await new Promise((r) => setTimeout(r, 1000))
        tokens = await refreshAccessToken(refreshToken)
      }
      setTokens(tokens)
      return tokens
    })().finally(() => {
      refreshPromise = null
    })
  }
  return refreshPromise
}

// Response error interceptor - handle token refresh with retry (exported for testing)
export async function handleResponseError(error: AxiosError): Promise<never> {
  const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

  // EP-160 section 2.3: a support session never refreshes and never goes to
  // /login. A 401 means the session ended, expired or lost its grant, and the
  // tab shows "เซสชันหมดอายุ".
  if (isSupportSession()) {
    if (error.response?.status === 401) markSupportSessionExpired()
    return Promise.reject(error)
  }

  // If 401 and we have a refresh token, try to refresh
  if (error.response?.status === 401 && !originalRequest._retry) {
    if (hasRefreshToken()) {
      originalRequest._retry = true

      try {
        const tokens = await refreshTokensSingleFlight()

        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${tokens.access_token}`
        }
        return api(originalRequest) as never
      } catch {
        // Refresh (incl. its retry) failed — clear tokens and go to login
        clearTokens()
        window.location.href = '/login'
        return Promise.reject(error)
      }
    }
  }

  return Promise.reject(error)
}

// EP-160: the support session error helpers live in their own module (many
// tests replace this one with a factory); re-exported here for callers.
export {
  apiErrorCode,
  isSearchRequest,
  isSupportSessionBlocked,
  supportSessionErrorCode,
  SUPPORT_SESSION_ERROR_CODES,
  type SupportSessionErrorCode,
} from '@/lib/supportSessionErrors'

api.interceptors.request.use(addAuthHeader, (error) => Promise.reject(error))
api.interceptors.response.use((response) => response, handleResponseError)

// Auth API functions.
// The web SPA is a PUBLIC OAuth client (Doorkeeper confidential=false) — a
// browser bundle cannot keep a secret, so token calls send client_id only (WEB-H2).
export const login = async (
  email: string,
  password: string,
  clientId: string
): Promise<AuthTokens> => {
  const response = await axios.post<AuthTokens>(`${API_BASE_URL}/oauth/token`, {
    grant_type: 'password',
    email: email,
    password,
    client_id: clientId,
  })
  return response.data
}

export const refreshAccessToken = async (refreshToken: string): Promise<AuthTokens> => {
  const clientId = import.meta.env.VITE_CLIENT_ID

  const response = await axios.post<AuthTokens>(`${API_BASE_URL}/oauth/token`, {
    grant_type: 'refresh_token',
    refresh_token: refreshToken,
    client_id: clientId,
  })
  return response.data
}

export const logout = async () => {
  // EP-160: in a support tab, end the session. The user tokens are not this
  // tab's to revoke or clear.
  if (isSupportSession()) {
    await endSupportSession()
    return
  }
  try {
    // QA B-004: a public Doorkeeper client must send client_id or revoke returns
    // 403 (the token is still cleared client-side, but the server session lingers).
    await api.post('/oauth/revoke', {
      token: accessToken,
      client_id: import.meta.env.VITE_CLIENT_ID,
    })
  } catch {
    // Ignore errors on logout
  } finally {
    clearTokens()
  }
}

export default api
